/**
 * Machinerie des fenetres d'imputation : qui peut etre observe, et qu'y observe-t-on.
 *
 * C'est ici que se joue la validite de toute l'analyse. Trois regles :
 *
 * 1. **Imputation sur le debut, pas sur la duree.** Un episode de cinq jours
 *    n'incrimine que les repas des 6-24 h precedant son declenchement, pas les
 *    quinze repas qu'il recouvre ensuite.
 *
 * 2. **Censure des repas non observables.** Si un episode du meme symptome est
 *    deja actif au debut de la fenetre, aucun « nouveau debut » ne peut y etre
 *    constate : le repas est retire du denominateur. Sans cette regle, une
 *    poussee de sept jours ferait compter des dizaines de repas en « pas de
 *    symptome » et diluerait tout signal reel.
 *
 * 3. **Censure a droite.** Un repas pris il y a trois heures n'a pas encore de
 *    verdict : sa fenetre n'est pas echue. L'inclure reviendrait a le compter
 *    systematiquement comme « sans symptome ».
 */

export interface EpisodeSimple {
  debut: number
  /** `null` = episode toujours en cours. */
  fin: number | null
}

export interface IssuesSymptome {
  /** 1 si le repas est exploitable pour ce symptome. */
  eligible: Uint8Array
  /** 1 si un nouvel episode a debute dans la fenetre du repas. */
  onset: Uint8Array
  /** Index de l'episode declenche, ou -1. Sert a compter les episodes distincts. */
  episodeIdx: Int32Array
  nbEligibles: number
  nbOnsets: number
}

/** Trie une copie des episodes par date de debut croissante. */
export function trierEpisodes(episodes: readonly EpisodeSimple[]): EpisodeSimple[] {
  return [...episodes].sort((a, b) => a.debut - b.debut)
}

/**
 * Maximum courant des dates de fin, dans l'ordre des debuts.
 *
 * Permet de repondre en O(log n) a « un episode est-il actif a l'instant tau ? » :
 * il suffit que le max des fins parmi les episodes commences avant tau depasse tau.
 */
export function prefixeMaxFin(episodesTries: readonly EpisodeSimple[]): Float64Array {
  const n = episodesTries.length
  const out = new Float64Array(n)
  let max = -Infinity
  for (let i = 0; i < n; i++) {
    const f = episodesTries[i]!.fin
    const v = f === null ? Infinity : f
    if (v > max) max = v
    out[i] = max
  }
  return out
}

/**
 * Calcule, pour chaque repas, s'il est exploitable et s'il est suivi d'un
 * declenchement.
 *
 * @param tRepas      instants des repas (ms epoch), tries croissants
 * @param episodesTries episodes du symptome etudie, tries par debut
 * @param maxFin      prefixe des fins, cf. `prefixeMaxFin`
 * @param w0          debut de fenetre en ms apres le repas
 * @param w1          fin de fenetre en ms apres le repas
 * @param finObs      instant au-dela duquel on n'observe plus rien
 */
export function calculerIssues(
  tRepas: Float64Array,
  episodesTries: readonly EpisodeSimple[],
  maxFin: Float64Array,
  w0: number,
  w1: number,
  finObs: number,
): IssuesSymptome {
  const n = tRepas.length
  const eligible = new Uint8Array(n)
  const onset = new Uint8Array(n)
  const episodeIdx = new Int32Array(n).fill(-1)
  let nbEligibles = 0
  let nbOnsets = 0
  const nbEpisodes = episodesTries.length

  // Balayage a deux pointeurs plutot que deux dichotomies par repas.
  //
  // `tRepas` etant trie, `debutFenetre` croit avec `i` : les deux index
  // recherches sont donc monotones et n'ont jamais a reculer. On passe de
  // O(n log E) a O(n + E) par appel — ce qui compte, car cette fonction est
  // rejouee pour chaque fenetre a chaque tirage de permutation, soit des
  // dizaines de milliers de fois par analyse.
  //
  //  - `k` : plus grand index tel que `debut <= debutFenetre`, ou -1 ;
  //  - `j` : plus petit index tel que `debut >= debutFenetre`.
  // Les deux sont maintenus separement : ils ne different pas seulement d'une
  // unite lorsqu'un episode debute exactement a l'ouverture de la fenetre.
  let k = -1
  let j = 0

  for (let i = 0; i < n; i++) {
    const debutFenetre = tRepas[i]! + w0
    const finFenetre = tRepas[i]! + w1

    // Regle 3 : fenetre non echue. `finFenetre` etant croissant, tous les
    // repas suivants le sont aussi — inutile de poursuivre.
    if (finFenetre > finObs) break

    while (k + 1 < nbEpisodes && episodesTries[k + 1]!.debut <= debutFenetre) k++
    while (j < nbEpisodes && episodesTries[j]!.debut < debutFenetre) j++

    // Regle 2 : un episode est-il deja actif a l'ouverture de la fenetre ?
    if (k >= 0 && maxFin[k]! > debutFenetre) continue

    eligible[i] = 1
    nbEligibles++

    // Regle 1 : un episode debute-t-il dans la fenetre ?
    if (j < nbEpisodes && episodesTries[j]!.debut <= finFenetre) {
      onset[i] = 1
      episodeIdx[i] = j
      nbOnsets++
    }
  }

  return { eligible, onset, episodeIdx, nbEligibles, nbOnsets }
}

/**
 * Decale toute la chronologie des symptomes de `delta`, circulairement sur
 * la periode d'observation `[t0, t0 + T]`.
 *
 * C'est le coeur du test de permutation. Un simple melange aleatoire des
 * etiquettes detruirait le fait que les symptomes arrivent **par grappes** et
 * produirait une distribution nulle bien trop optimiste. Le decalage circulaire,
 * lui, conserve le nombre d'episodes, leurs durees et leur regroupement
 * temporel ; il ne casse que l'alignement avec les repas — exactement
 * l'hypothese que l'on veut tester.
 *
 * Un episode qui deborde la fin de periode est **reinjecte au debut** afin que
 * la duree totale de symptomes reste rigoureusement constante d'une permutation
 * a l'autre.
 */
export function decalerEpisodes(
  episodesTries: readonly EpisodeSimple[],
  delta: number,
  t0: number,
  T: number,
): EpisodeSimple[] {
  if (T <= 0) return [...episodesTries]
  const out: EpisodeSimple[] = []

  for (const e of episodesTries) {
    const duree = e.fin === null ? null : e.fin - e.debut
    const rel = (((e.debut - t0 + delta) % T) + T) % T
    const debut = t0 + rel
    if (duree === null) {
      out.push({ debut, fin: null })
      continue
    }
    const fin = debut + duree
    const depassement = fin - (t0 + T)
    if (depassement > 0) {
      out.push({ debut, fin: t0 + T })
      // Portion reinjectee au debut de la periode.
      out.push({ debut: t0, fin: t0 + Math.min(depassement, T) })
    } else {
      out.push({ debut, fin })
    }
  }

  out.sort((a, b) => a.debut - b.debut)
  return out
}
